import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getSales, createSale, updateSale, deleteSale, bulkUpdateSalesSnapshot, getProjects, getContracts, getUsers, getAllUsersForDisplay, getUsersAvailableForEntry, getActivityTypes, getProducts, getBusinessUnits, getCustomFields } from '../lib/api';
import { CustomFieldsSection } from '../components/EntityCustomFields';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Combobox } from '../components/ui/combobox';
import { MultiSelect } from '../components/ui/multi-select';
import { SearchableCheckboxList } from '../components/ui/searchable-checkbox-list';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Plus, MoreHorizontal, Pencil, Trash2, ShoppingCart, Search, Loader2, TrendingUp, Euro, Calendar, RotateCcw, ArrowUpDown, ArrowUp, ArrowDown, CheckSquare, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { format, parseISO, startOfMonth, endOfMonth, subMonths, startOfQuarter, endOfQuarter, subQuarters, startOfYear, endOfYear, subYears } from 'date-fns';

// Date presets
const DATE_PRESETS = [
  { 
    value: 'this_month', 
    label: 'Questo mese',
    getValue: () => ({
      start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
      end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
    })
  },
  { 
    value: 'last_month', 
    label: 'Mese scorso',
    getValue: () => ({
      start: format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'),
      end: format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd')
    })
  },
  { 
    value: 'this_quarter', 
    label: 'Questo trimestre',
    getValue: () => ({
      start: format(startOfQuarter(new Date()), 'yyyy-MM-dd'),
      end: format(endOfQuarter(new Date()), 'yyyy-MM-dd')
    })
  },
  { 
    value: 'last_quarter', 
    label: 'Trimestre scorso',
    getValue: () => ({
      start: format(startOfQuarter(subQuarters(new Date(), 1)), 'yyyy-MM-dd'),
      end: format(endOfQuarter(subQuarters(new Date(), 1)), 'yyyy-MM-dd')
    })
  },
  { 
    value: 'this_year', 
    label: "Quest'anno",
    getValue: () => ({
      start: format(startOfYear(new Date()), 'yyyy-MM-dd'),
      end: format(endOfYear(new Date()), 'yyyy-MM-dd')
    })
  },
  { 
    value: 'last_year', 
    label: 'Anno scorso',
    getValue: () => ({
      start: format(startOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd'),
      end: format(endOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd')
    })
  },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);

export default function SalesPage() {
  const { user, hasRole } = useAuth();
  const [sales, setSales] = useState([]);
  const [projects, setProjects] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [venditori, setVenditori] = useState([]); // Tutti gli utenti (per display storico)
  const [availableVenditori, setAvailableVenditori] = useState([]); // Utenti selezionabili per inserimento
  const [pmUsers, setPmUsers] = useState([]); // Lista PM per selezione
  const [supervisorUsers, setSupervisorUsers] = useState([]); // Lista Supervisor per selezione
  const [activityTypes, setActivityTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filtri avanzati
  const [filterProjects, setFilterProjects] = useState([]); // Progetti - multi-select
  const [filterVenditori, setFilterVenditori] = useState([]); // Operativi - multi-select
  const [filterPMs, setFilterPMs] = useState([]); // Project Manager - multi-select
  const [filterBUs, setFilterBUs] = useState([]); // Business Unit - multi-select
  const [filterProducts, setFilterProducts] = useState([]); // Prodotti - multi-select
  const [datePreset, setDatePreset] = useState(''); // Preset data selezionato
  const [dateRange, setDateRange] = useState({ start: '', end: '' }); // Range date personalizzato
  
  // Ordinamento
  const [sortColumn, setSortColumn] = useState('sale_date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight') || null;

  // When linked from another page with ?highlight=<sale_id>, scroll the row into view
  useEffect(() => {
    if (!highlightId || loading) return;
    const scrollTimer = setTimeout(() => {
      const el = document.getElementById(`sale-row-${highlightId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 400);
    const clearTimer = setTimeout(() => {
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        params.delete('highlight');
        return params;
      }, { replace: true });
    }, 6000);
    return () => { clearTimeout(scrollTimer); clearTimeout(clearTimer); };
  }, [highlightId, loading, setSearchParams]);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkFormData, setBulkFormData] = useState({
    pm_ids: null,
    supervisor_ids: null,
    contract_id: null,
    product_id: null,
  });

  const [formData, setFormData] = useState({
    project_id: '',
    activity_type_id: '',
    amount: '',
    description: '',
    sale_date: new Date().toISOString().split('T')[0],
    commission_mode: 'default',
    custom_commission_type: 'percentage',
    custom_commission_value: '',
    venditore_id: '', // Per admin/project_manager
    pm_ids: [], // Per admin - selezione PM
    supervisor_ids: [], // Per admin - selezione Supervisor
    product_id: '', // Per admin - selezione Prodotto snapshot
    contract_ids: [], // Per admin - selezione Contratti snapshot (legacy, mantenuto per compatibilità)
    contract_id: '', // Per admin - selezione Contratto snapshot (singolo)
    custom_fields: {},
  });
  const [sfCustomFields, setSfCustomFields] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [salesRes, projectsRes, contractsRes, activityTypesRes, productsRes, businessUnitsRes] = await Promise.all([
        getSales(),
        getProjects(),
        getContracts(),
        getActivityTypes(),
        getProducts(),
        getBusinessUnits(),
      ]);
      setSales(salesRes.data);
      setProjects(projectsRes.data);
      setContracts(contractsRes.data);
      setActivityTypes(activityTypesRes.data);
      setProducts(productsRes.data);
      setBusinessUnits(businessUnitsRes.data);
      getCustomFields('success_fee').then(r => setSfCustomFields(r.data || [])).catch(() => {});

      // Carica TUTTI gli utenti (inclusi disattivati) per mostrare i nomi corretti
      // Questo è necessario per la risoluzione dei nomi PM e venditori nella cronologia
      const allUsersRes = await getAllUsersForDisplay();
      setVenditori(allUsersRes.data);
      
      // Carica gli utenti disponibili per l'inserimento (filtrati per ruolo)
      const availableRes = await getUsersAvailableForEntry();
      setAvailableVenditori(availableRes.data);
      
      // Filtra solo i PM attivi per la selezione
      const activePMs = allUsersRes.data.filter(u => 
        (u.role === 'project_manager' || (u.roles && u.roles.includes('project_manager'))) && 
        u.is_active !== false
      );
      setPmUsers(activePMs);
      
      // Filtra i Supervisor attivi per la selezione
      const activeSupervisors = allUsersRes.data.filter(u => 
        (u.role === 'supervisor' || (u.roles && u.roles.includes('supervisor'))) && 
        u.is_active !== false
      );
      setSupervisorUsers(activeSupervisors);
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile caricare i dati', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Trova la tipologia "Vendite" - le vendite usano sempre questa tipologia automaticamente
  const getVenditeActivityType = () => {
    return activityTypes.find(at => at.name.toLowerCase() === 'vendite' || at.name.toLowerCase() === 'vendita');
  };

  const handleOpenDialog = (sale = null) => {
    if (sale) {
      setEditingSale(sale);
      
      setFormData({
        project_id: sale.project_id,
        activity_type_id: sale.activity_type_id || '',
        amount: sale.amount.toString(),
        description: sale.description || '',
        sale_date: sale.sale_date?.split('T')[0] || '',
        commission_mode: sale.commission_mode || 'default',
        custom_commission_type: sale.custom_commission_type || 'percentage',
        custom_commission_value: sale.custom_commission_value?.toString() || '',
        venditore_id: sale.venditore_id || '',
        pm_ids: sale.pm_ids_snapshot || [],
        supervisor_ids: sale.supervisor_ids_snapshot || [],
        product_id: sale.product_id_snapshot || '',
        contract_ids: sale.contract_id_snapshot ? [sale.contract_id_snapshot] : [],
        contract_id: sale.contract_id_snapshot || '',
        custom_fields: sale.custom_fields || {},
      });
    } else {
      setEditingSale(null);
      setFormData({
        project_id: '',
        activity_type_id: '',
        amount: '',
        description: '',
        sale_date: new Date().toISOString().split('T')[0],
        commission_mode: 'default',
        custom_commission_type: 'percentage',
        custom_commission_value: '',
        venditore_id: '',
        pm_ids: [],
        supervisor_ids: [],
        product_id: '',
        contract_ids: [],
        contract_id: '',
        custom_fields: {},
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Per le vendite, usa sempre la tipologia "Vendite" automaticamente
      const venditeType = getVenditeActivityType();
      if (!venditeType) {
        toast({ 
          title: 'Errore', 
          description: 'Tipologia "Vendite" non trovata. Configurala in Impostazioni → Tipologie Attività.', 
          variant: 'destructive' 
        });
        setSaving(false);
        return;
      }

      const missingCF = sfCustomFields.filter(f => f.is_required && [undefined, null, ''].includes(formData.custom_fields?.[f.id]));
      if (missingCF.length) {
        toast({ title: 'Campi obbligatori mancanti', description: missingCF.map(f => f.name).join(', '), variant: 'destructive' });
        setSaving(false);
        return;
      }

      const data = {
        custom_fields: formData.custom_fields || {},
        project_id: formData.project_id,
        activity_type_id: venditeType.id, // Usa sempre la tipologia "Vendite"
        amount: parseFloat(formData.amount),
        description: formData.description || null,
        sale_date: new Date(formData.sale_date).toISOString(),
        commission_mode: formData.commission_mode,
        custom_commission_type: formData.commission_mode === 'custom' ? formData.custom_commission_type : null,
        custom_commission_value: formData.commission_mode === 'custom' ? parseFloat(formData.custom_commission_value) : null,
      };

      // Aggiungi venditore_id solo per admin/project_manager
      if (hasRole(['admin', 'hr', 'project_manager']) && formData.venditore_id) {
        data.venditore_id = formData.venditore_id;
      }

      // Admin/HR può modificare i PM (in modifica)
      if (hasRole(['admin', 'hr']) && editingSale) {
        data.pm_ids_snapshot = formData.pm_ids;
      }

      // Admin/HR può modificare i Supervisor (in modifica)
      if (hasRole(['admin', 'hr']) && editingSale) {
        data.supervisor_ids_snapshot = formData.supervisor_ids;
      }

      // Admin/HR può modificare il Prodotto snapshot (in modifica)
      if (hasRole(['admin', 'hr']) && editingSale && formData.product_id) {
        data.product_id_snapshot = formData.product_id;
      }

      // Admin/HR può modificare il Contratto snapshot (in modifica)
      // Usa il primo contratto selezionato da contract_ids array
      if (hasRole(['admin', 'hr']) && editingSale) {
        const selectedContract = formData.contract_ids && formData.contract_ids.length > 0 
          ? formData.contract_ids[0] 
          : null;
        if (selectedContract) {
          data.contract_id_snapshot = selectedContract;
        }
      }

      if (editingSale) {
        await updateSale(editingSale.id, data);
        toast({ title: 'Vendita aggiornata' });
      } else {
        await createSale(data);
        toast({ title: 'Vendita creata' });
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa vendita?')) return;
    try {
      await deleteSale(id);
      toast({ title: 'Vendita eliminata' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile eliminare la vendita', variant: 'destructive' });
    }
  };

  // Bulk selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedSales.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedSales.map(s => s.id)));
    }
  };

  const openBulkDialog = () => {
    setBulkFormData({ pm_ids: null, supervisor_ids: null, contract_id: null, product_id: null });
    setBulkDialogOpen(true);
  };

  const handleBulkUpdate = async () => {
    setBulkSaving(true);
    try {
      const payload = { ids: Array.from(selectedIds) };
      if (bulkFormData.pm_ids !== null) payload.pm_ids_snapshot = bulkFormData.pm_ids;
      if (bulkFormData.supervisor_ids !== null) payload.supervisor_ids_snapshot = bulkFormData.supervisor_ids;
      if (bulkFormData.contract_id !== null) payload.contract_id_snapshot = bulkFormData.contract_id;
      if (bulkFormData.product_id !== null) payload.product_id_snapshot = bulkFormData.product_id;

      const res = await bulkUpdateSalesSnapshot(payload);
      toast({ title: res.data.message || `${selectedIds.size} vendite aggiornate` });
      setSelectedIds(new Set());
      setBulkDialogOpen(false);
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Errore durante l\'aggiornamento massivo', variant: 'destructive' });
    } finally {
      setBulkSaving(false);
    }
  };

  const getProjectName = (projectId) => projects.find((p) => p.id === projectId)?.name || 'N/A';
  
  // Funzione per ottenere la commissione di default del progetto
  const getProjectDefaultCommission = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project || !project.commission) return null;
    const { commission_type, value } = project.commission;
    if (!value) return null;
    return {
      type: commission_type,
      value: value,
      label: commission_type === 'percentage' ? `${value}%` : `€${value}`
    };
  };
  
  const getVenditore = (venditoreId) => {
    if (!venditoreId) return 'N/A';
    const venditore = venditori.find((v) => v.id === venditoreId);
    if (!venditore) return '(Utente non trovato)';
    return venditore.is_active === false ? `${venditore.name} (disattivato)` : venditore.name;
  };
  
  // Funzione per ottenere i nomi dei PM dallo snapshot
  const getPmNamesFromSnapshot = (pmIdsSnapshot) => {
    if (!pmIdsSnapshot || pmIdsSnapshot.length === 0) return '-';
    const pmNames = pmIdsSnapshot.map(pmId => {
      const pm = venditori.find(v => v.id === pmId);
      if (!pm) return '(PM non trovato)';
      return pm.is_active === false ? `${pm.name} (disattivato)` : pm.name;
    });
    return pmNames.join(', ');
  };

  // Funzione per ottenere i nomi dei Supervisor dallo snapshot
  const getSupervisorNamesFromSnapshot = (supervisorIdsSnapshot) => {
    if (!supervisorIdsSnapshot || supervisorIdsSnapshot.length === 0) return '-';
    const supNames = supervisorIdsSnapshot.map(supId => {
      const sup = venditori.find(v => v.id === supId);
      if (!sup) return '(Supervisor non trovato)';
      return sup.is_active === false ? `${sup.name} (disattivato)` : sup.name;
    });
    return supNames.join(', ');
  };

  // Funzione per ottenere i nomi dei Contratti dallo snapshot
  const getContractNamesFromSnapshot = (sale) => {
    // Usa il nuovo campo singolo contract_id_snapshot
    if (sale.contract_id_snapshot) {
      const contract = contracts.find(c => c.id === sale.contract_id_snapshot);
      return contract?.name || sale.contract_name_snapshot || '(Contratto non trovato)';
    }
    return null; // Nessun contratto associato
  };

  // Funzione per ottenere info prodotto
  const getProductInfo = (sale) => {
    if (sale.product_name_snapshot) {
      return {
        name: sale.product_name_snapshot,
        bu: sale.business_unit_name_snapshot || '-'
      };
    }
    return null;
  };

  // Handle date preset change
  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset && preset !== 'custom') {
      const presetConfig = DATE_PRESETS.find(p => p.value === preset);
      if (presetConfig) {
        setDateRange(presetConfig.getValue());
      }
    } else if (preset === 'custom') {
      // Keep current custom range
    } else {
      setDateRange({ start: '', end: '' });
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setFilterProjects([]);
    setFilterVenditori([]);
    setFilterPMs([]);
    setFilterBUs([]);
    setFilterProducts([]);
    setDatePreset('');
    setDateRange({ start: '', end: '' });
  };

  // Check if any filter is active
  const hasActiveFilters = searchTerm || filterProjects.length > 0 || 
    filterVenditori.length > 0 || filterPMs.length > 0 || filterBUs.length > 0 || filterProducts.length > 0 || dateRange.start || dateRange.end;

  // Handle sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" /> 
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const filteredSales = useMemo(() => {
    return sales.filter((s) => {
      // Search filter
      const matchesSearch = !searchTerm || 
        getProjectName(s.project_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getVenditore(s.venditore_id).toLowerCase().includes(searchTerm.toLowerCase());
      
      // Projects filter
      const matchesProject = filterProjects.length === 0 || filterProjects.includes(s.project_id);
      
      // Venditore filter
      const matchesVenditore = filterVenditori.length === 0 || filterVenditori.includes(s.venditore_id);
      
      // PM filter
      const matchesPM = filterPMs.length === 0 || 
        (s.pm_ids_snapshot && s.pm_ids_snapshot.some(pmId => filterPMs.includes(pmId)));
      
      // BU filter
      const matchesBU = filterBUs.length === 0 || 
        (s.business_unit_name_snapshot && filterBUs.some(buId => {
          const bu = businessUnits.find(b => b.id === buId);
          return bu && bu.name === s.business_unit_name_snapshot;
        }));
      
      // Product filter
      const matchesProduct = filterProducts.length === 0 ||
        (s.product_id_snapshot && filterProducts.includes(s.product_id_snapshot));
      
      // Date filter
      let matchesDate = true;
      if (dateRange.start || dateRange.end) {
        const saleDate = s.sale_date ? new Date(s.sale_date) : null;
        if (saleDate) {
          const startDate = dateRange.start ? new Date(dateRange.start) : new Date('1900-01-01');
          const endDate = dateRange.end ? new Date(dateRange.end) : new Date('2100-12-31');
          endDate.setHours(23, 59, 59, 999);
          matchesDate = saleDate >= startDate && saleDate <= endDate;
        }
      }
      
      return matchesSearch && matchesProject && matchesVenditore && matchesPM && matchesBU && matchesProduct && matchesDate;
    });
  }, [sales, searchTerm, filterProjects, filterVenditori, filterPMs, filterBUs, filterProducts, dateRange, projects, venditori, businessUnits]);

  // Sort filtered sales
  const sortedSales = useMemo(() => {
    return [...filteredSales].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortColumn) {
        case 'sale_date':
          aValue = a.sale_date ? new Date(a.sale_date) : new Date(0);
          bValue = b.sale_date ? new Date(b.sale_date) : new Date(0);
          break;
        case 'project':
          aValue = getProjectName(a.project_id)?.toLowerCase() || '';
          bValue = getProjectName(b.project_id)?.toLowerCase() || '';
          break;
        case 'venditore':
          aValue = getVenditore(a.venditore_id)?.toLowerCase() || '';
          bValue = getVenditore(b.venditore_id)?.toLowerCase() || '';
          break;
        case 'amount':
          aValue = a.amount || 0;
          bValue = b.amount || 0;
          break;
        case 'commission':
          aValue = a.calculated_commission || 0;
          bValue = b.calculated_commission || 0;
          break;
        default:
          aValue = a.sale_date ? new Date(a.sale_date) : new Date(0);
          bValue = b.sale_date ? new Date(b.sale_date) : new Date(0);
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [filteredSales, sortColumn, sortDirection]);

  const totalSales = filteredSales.reduce((sum, s) => sum + s.amount, 0);
  const totalCommissions = filteredSales.reduce((sum, s) => sum + s.calculated_commission, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendite</h1>
          <p className="text-gray-500">Gestisci le vendite e i Success Fee</p>
        </div>
        {hasRole(['admin', 'hr', 'venditore', 'project_manager']) && (
          <Button onClick={() => handleOpenDialog()} data-testid="add-sale-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuova Vendita
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Vendite Totali</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalSales)}</div>
            <p className="text-xs text-muted-foreground">{filteredSales.length} vendite</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Fee</CardTitle>
            <Euro className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalCommissions)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Media Success Fee</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredSales.length > 0 ? formatCurrency(totalCommissions / filteredSales.length) : formatCurrency(0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            {/* Prima riga: Ricerca e Periodo */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Cerca per progetto, descrizione o venditore..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Filtro Data con Preset */}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <Select value={datePreset || "all"} onValueChange={(v) => handleDatePresetChange(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Periodo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutto il periodo</SelectItem>
                    {DATE_PRESETS.map((preset) => (
                      <SelectItem key={preset.value} value={preset.value}>{preset.label}</SelectItem>
                    ))}
                    <SelectItem value="custom">Personalizzato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Date Range personalizzato */}
              {datePreset === 'custom' && (
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    className="w-[140px]"
                  />
                  <span className="text-gray-400">-</span>
                  <Input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    className="w-[140px]"
                  />
                </div>
              )}
              
              {/* Reset filters */}
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={resetFilters} className="text-gray-500">
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Reset
                </Button>
              )}
            </div>
            
            {/* Seconda riga: Filtri Multi-select */}
            <div className="flex flex-wrap gap-3">
              {/* Progetti - Multi-select */}
              <div className="w-[200px]">
                <MultiSelect
                  options={projects.map(p => ({ value: p.id, label: p.name }))}
                  selected={filterProjects}
                  onChange={setFilterProjects}
                  placeholder="Progetti"
                />
              </div>
              
              {/* Operativo - Multi-select */}
              <div className="w-[200px]">
                <MultiSelect
                  options={venditori.filter(v => v.is_active !== false).map(v => ({ value: v.id, label: v.name }))}
                  selected={filterVenditori}
                  onChange={setFilterVenditori}
                  placeholder="Operativo"
                />
              </div>
              
              {/* Project Manager - Multi-select */}
              <div className="w-[200px]">
                <MultiSelect
                  options={pmUsers.map(pm => ({ value: pm.id, label: pm.name }))}
                  selected={filterPMs}
                  onChange={setFilterPMs}
                  placeholder="Project Manager"
                />
              </div>
              
              {/* BU - Multi-select */}
              <div className="w-[200px]">
                <MultiSelect
                  options={businessUnits.map(bu => ({ value: bu.id, label: bu.name }))}
                  selected={filterBUs}
                  onChange={setFilterBUs}
                  placeholder="Business Unit"
                />
              </div>
              
              {/* Prodotti - Multi-select */}
              <div className="w-[200px]">
                <MultiSelect
                  options={products.filter(p => p.is_active !== false).map(p => ({ value: p.id, label: p.name }))}
                  selected={filterProducts}
                  onChange={setFilterProducts}
                  placeholder="Prodotti"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk actions bar */}
      {hasRole(['admin']) && selectedIds.size > 0 && (
        <div className="sticky top-0 z-20 bg-blue-600 text-white rounded-lg p-3 flex items-center justify-between shadow-lg" data-testid="bulk-actions-bar">
          <div className="flex items-center gap-4">
            <CheckSquare className="w-5 h-5" />
            <span className="font-medium">{selectedIds.size} {selectedIds.size === 1 ? 'vendita selezionata' : 'vendite selezionate'}</span>
            <span className="text-blue-200">|</span>
            <span className="text-sm">Totale QTY: <strong>{selectedIds.size}</strong></span>
            <span className="text-blue-200">|</span>
            <span className="text-sm">Totale: <strong>{formatCurrency(sortedSales.filter(s => selectedIds.has(s.id)).reduce((sum, s) => sum + (s.amount || 0), 0))}</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={openBulkDialog} data-testid="bulk-edit-btn">
              <Pencil className="w-4 h-4 mr-1" />
              Modifica Snapshot
            </Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-blue-700" onClick={() => setSelectedIds(new Set())}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Sales Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista Vendite</CardTitle>
        </CardHeader>
        <CardContent>
          {sortedSales.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nessuna vendita trovata</h3>
              <p className="mt-1 text-sm text-gray-500">Inizia creando una nuova vendita.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {hasRole(['admin']) && (
                    <TableHead className="w-10">
                      <Checkbox
                        checked={sortedSales.length > 0 && selectedIds.size === sortedSales.length}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-sales"
                      />
                    </TableHead>
                  )}
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('sale_date')}
                  >
                    <div className="flex items-center">
                      Data
                      {getSortIcon('sale_date')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('project')}
                  >
                    <div className="flex items-center">
                      Progetto
                      {getSortIcon('project')}
                    </div>
                  </TableHead>
                  <TableHead>Prodotto</TableHead>
                  <TableHead>BU</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('venditore')}
                  >
                    <div className="flex items-center">
                      Venditore
                      {getSortIcon('venditore')}
                    </div>
                  </TableHead>
                  <TableHead>Project Manager</TableHead>
                  <TableHead>Supervisor</TableHead>
                  <TableHead>Contratto</TableHead>
                  <TableHead>QTY</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('amount')}
                  >
                    <div className="flex items-center">
                      Importo
                      {getSortIcon('amount')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('commission')}
                  >
                    <div className="flex items-center">
                      Success Fee
                      {getSortIcon('commission')}
                    </div>
                  </TableHead>
                  {hasRole(['admin', 'hr', 'project_manager', 'venditore']) && <TableHead className="w-12"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedSales.map((sale) => {
                  const productInfo = getProductInfo(sale);
                  const contractName = getContractNamesFromSnapshot(sale);
                  const isMissingContract = sale.missing_contract || !contractName;
                  
                  return (
                    <TableRow
                      key={sale.id}
                      id={`sale-row-${sale.id}`}
                      className={`${isMissingContract ? 'bg-amber-50' : ''} ${selectedIds.has(sale.id) ? 'bg-blue-50' : ''} ${highlightId === sale.id ? 'ring-2 ring-orange-400 ring-inset bg-orange-50' : ''}`}
                    >
                      {hasRole(['admin']) && (
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(sale.id)}
                            onCheckedChange={() => toggleSelect(sale.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        {sale.sale_date ? format(parseISO(sale.sale_date), 'dd/MM/yyyy') : '-'}
                      </TableCell>
                      <TableCell className="font-medium">{getProjectName(sale.project_id)}</TableCell>
                      <TableCell>
                        {productInfo ? (
                          <span className="text-sm">{productInfo.name}</span>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {productInfo?.bu ? (
                          <Badge variant="outline" className="text-xs">{productInfo.bu}</Badge>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>{getVenditore(sale.venditore_id)}</TableCell>
                      <TableCell className="text-purple-600">
                        {getPmNamesFromSnapshot(sale.pm_ids_snapshot)}
                      </TableCell>
                      <TableCell className="text-indigo-600">
                        {getSupervisorNamesFromSnapshot(sale.supervisor_ids_snapshot)}
                      </TableCell>
                      <TableCell>
                        {contractName ? (
                          <span className="text-blue-600">{contractName}</span>
                        ) : (
                          <span className="text-amber-600 flex items-center gap-1">
                            <span>⚠️</span>
                            <span className="text-xs">Nessun contratto</span>
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">1</TableCell>
                      <TableCell>{formatCurrency(sale.amount)}</TableCell>
                      <TableCell className="text-green-600 font-medium">
                        {formatCurrency(sale.calculated_commission)}
                      </TableCell>
                      {hasRole(['admin', 'hr', 'project_manager', 'venditore']) && (
                        <TableCell>
                          <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => handleOpenDialog(sale)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Modifica
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(sale.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto [&>form]:flex [&>form]:flex-col">
          <DialogHeader>
            <DialogTitle>{editingSale ? 'Modifica Vendita' : 'Nuova Vendita'}</DialogTitle>
            <DialogDescription>
              {editingSale ? 'Modifica i dettagli della vendita' : 'Crea una nuova vendita'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Campo Venditore - solo per admin, HR e project_manager */}
            {hasRole(['admin', 'hr', 'project_manager']) && (
              <div className="space-y-2">
                <Label>Venditore</Label>
                <Combobox
                  options={[
                    { value: 'me', label: 'Me stesso' },
                    ...availableVenditori.filter(v => v.is_active !== false).map((v) => ({
                      value: v.id,
                      label: `${v.name} (${v.email})`
                    }))
                  ]}
                  value={formData.venditore_id || 'me'}
                  onChange={(v) => setFormData({ ...formData, venditore_id: v === 'me' ? '' : v })}
                  placeholder="Seleziona venditore"
                  searchPlaceholder="Cerca venditore..."
                  emptyText="Nessun venditore trovato"
                />
                {hasRole(['project_manager']) && !hasRole(['admin', 'hr']) && (
                  <p className="text-xs text-gray-500">
                    Puoi selezionare solo utenti assegnati ai tuoi progetti
                  </p>
                )}
              </div>
            )}

            {/* Campo Project Manager - solo Admin in modifica */}
            {hasRole(['admin', 'hr']) && editingSale && (
              <div className="space-y-2">
                <Label className="text-sm">Project Manager (snapshot)</Label>
                <SearchableCheckboxList
                  items={pmUsers}
                  selectedIds={formData.pm_ids}
                  onToggle={(id, checked) => {
                    if (checked) {
                      setFormData({ ...formData, pm_ids: [...formData.pm_ids, id] });
                    } else {
                      setFormData({ ...formData, pm_ids: formData.pm_ids.filter(i => i !== id) });
                    }
                  }}
                  idPrefix="pm"
                  placeholder="Cerca PM..."
                  emptyText="Nessun PM disponibile"
                />
              </div>
            )}

            {/* Campo Supervisor - solo Admin/HR in modifica */}
            {hasRole(['admin', 'hr']) && editingSale && (
              <div className="space-y-2">
                <Label className="text-sm">Supervisor (snapshot)</Label>
                <SearchableCheckboxList
                  items={supervisorUsers}
                  selectedIds={formData.supervisor_ids}
                  onToggle={(id, checked) => {
                    if (checked) {
                      setFormData({ ...formData, supervisor_ids: [...formData.supervisor_ids, id] });
                    } else {
                      setFormData({ ...formData, supervisor_ids: formData.supervisor_ids.filter(i => i !== id) });
                    }
                  }}
                  idPrefix="sup"
                  placeholder="Cerca supervisor..."
                  emptyText="Nessun Supervisor disponibile"
                />
              </div>
            )}

            {/* Campo Prodotto - solo Admin/HR in modifica */}
            {hasRole(['admin', 'hr']) && editingSale && (
              <div className="space-y-2">
                <Label className="text-sm">Prodotto (snapshot)</Label>
                <Combobox
                  options={products.map((p) => ({ value: p.id, label: p.name }))}
                  value={formData.product_id}
                  onChange={(val) => setFormData({ ...formData, product_id: val })}
                  placeholder="Seleziona prodotto"
                  searchPlaceholder="Cerca prodotto..."
                  emptyText="Nessun prodotto trovato"
                />
              </div>
            )}

            {/* Campo Contratti - solo Admin/HR in modifica */}
            {hasRole(['admin', 'hr']) && editingSale && (
              <div className="space-y-2">
                <Label className="text-sm">Contratti (snapshot)</Label>
                <SearchableCheckboxList
                  items={contracts}
                  selectedIds={formData.contract_ids}
                  onToggle={(id, checked) => {
                    if (checked) {
                      setFormData({ ...formData, contract_ids: [...formData.contract_ids, id] });
                    } else {
                      setFormData({ ...formData, contract_ids: formData.contract_ids.filter(i => i !== id) });
                    }
                  }}
                  idPrefix="contract"
                  placeholder="Cerca contratto..."
                  emptyText="Nessun contratto disponibile"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Progetto *</Label>
              <Combobox
                options={projects.map(p => ({ value: p.id, label: p.name }))}
                value={formData.project_id}
                onChange={(v) => setFormData({ ...formData, project_id: v })}
                placeholder="Seleziona progetto"
                searchPlaceholder="Cerca progetto..."
                emptyText="Nessun progetto trovato"
              />
            </div>

            {/* Info tipologia: le vendite usano sempre la tipologia "Vendite" automaticamente */}
            {(() => {
              const venditeType = getVenditeActivityType();
              const typeProducts = venditeType && venditeType.product_ids 
                ? venditeType.product_ids.map(pid => products.find(p => p.id === pid)).filter(Boolean)
                : [];
              const typeBUs = [...new Set(typeProducts.map(p => businessUnits.find(b => b.id === p.business_unit_id)).filter(Boolean))];
              return venditeType ? (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Tipologia:</strong> {venditeType.name}
                    {typeProducts.length > 0 && <> | <strong>Prodott{typeProducts.length > 1 ? 'i' : 'o'}:</strong> {typeProducts.map(p => p.name).join(', ')}</>}
                    {typeBUs.length > 0 && <> | <strong>BU:</strong> {typeBUs.map(bu => bu.name).join(', ')}</>}
                  </p>
                </div>
              ) : (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    ⚠️ Tipologia "Vendite" non configurata. Vai in Impostazioni → Tipologie Attività.
                  </p>
                </div>
              );
            })()}

            <div className="space-y-2">
              <Label htmlFor="amount">Importo *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                onWheel={(e) => e.target.blur()}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sale_date">Data Vendita *</Label>
              <Input
                id="sale_date"
                type="date"
                value={formData.sale_date}
                onChange={(e) => setFormData({ ...formData, sale_date: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="border rounded-lg p-4 space-y-4">
              <h4 className="font-medium">Success Fee</h4>
              <div className="space-y-2">
                <Label>Modo Calcolo</Label>
                <Select
                  value={formData.commission_mode}
                  onValueChange={(v) => setFormData({ ...formData, commission_mode: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default (dal progetto)</SelectItem>
                    <SelectItem value="custom">Personalizzato</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Mostra la commissione di default quando selezionato */}
                {formData.commission_mode === 'default' && formData.project_id && (
                  <div className="mt-2">
                    {getProjectDefaultCommission(formData.project_id) ? (
                      <Badge variant="secondary" className="text-sm">
                        Default: {getProjectDefaultCommission(formData.project_id).label}
                      </Badge>
                    ) : (
                      <span className="text-sm text-amber-600">⚠️ Nessuna commissione configurata nel progetto</span>
                    )}
                  </div>
                )}
              </div>

              {formData.commission_mode === 'custom' && (
                <>
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select
                      value={formData.custom_commission_type}
                      onValueChange={(v) => setFormData({ ...formData, custom_commission_type: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentuale</SelectItem>
                        <SelectItem value="fixed">Importo Fisso</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>
                      Valore {formData.custom_commission_type === 'percentage' ? '(%)' : '(€)'}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.custom_commission_value}
                      onChange={(e) => setFormData({ ...formData, custom_commission_value: e.target.value })}
                      onWheel={(e) => e.target.blur()}
                    />
                  </div>
                </>
              )}
            </div>

            <CustomFieldsSection
              fields={sfCustomFields}
              values={formData.custom_fields}
              onChange={(cf) => setFormData(prev => ({ ...prev, custom_fields: cf }))}
              testIdPrefix="sale-cf"
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingSale ? 'Aggiorna' : 'Crea'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifica Massiva</DialogTitle>
            <DialogDescription>
              Modifica gli snapshot per {selectedIds.size} {selectedIds.size === 1 ? 'vendita' : 'vendite'}. Compila solo i campi che vuoi aggiornare.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Project Manager</Label>
                {bulkFormData.pm_ids !== null && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs text-gray-500" onClick={() => setBulkFormData(p => ({...p, pm_ids: null}))}>
                    Annulla modifica
                  </Button>
                )}
              </div>
              {bulkFormData.pm_ids === null ? (
                <Button variant="outline" size="sm" className="w-full text-gray-500" onClick={() => setBulkFormData(p => ({...p, pm_ids: []}))}>
                  Clicca per modificare PM
                </Button>
              ) : (
                <SearchableCheckboxList
                  items={pmUsers}
                  selectedIds={bulkFormData.pm_ids}
                  onToggle={(id, checked) => {
                    setBulkFormData(p => ({
                      ...p,
                      pm_ids: checked ? [...(p.pm_ids || []), id] : (p.pm_ids || []).filter(i => i !== id)
                    }));
                  }}
                  idPrefix="bulk-pm"
                  placeholder="Cerca PM..."
                  emptyText="Nessun PM disponibile"
                />
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Supervisor</Label>
                {bulkFormData.supervisor_ids !== null && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs text-gray-500" onClick={() => setBulkFormData(p => ({...p, supervisor_ids: null}))}>
                    Annulla modifica
                  </Button>
                )}
              </div>
              {bulkFormData.supervisor_ids === null ? (
                <Button variant="outline" size="sm" className="w-full text-gray-500" onClick={() => setBulkFormData(p => ({...p, supervisor_ids: []}))}>
                  Clicca per modificare Supervisor
                </Button>
              ) : (
                <SearchableCheckboxList
                  items={supervisorUsers}
                  selectedIds={bulkFormData.supervisor_ids}
                  onToggle={(id, checked) => {
                    setBulkFormData(p => ({
                      ...p,
                      supervisor_ids: checked ? [...(p.supervisor_ids || []), id] : (p.supervisor_ids || []).filter(i => i !== id)
                    }));
                  }}
                  idPrefix="bulk-sup"
                  placeholder="Cerca supervisor..."
                  emptyText="Nessun Supervisor disponibile"
                />
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Contratto</Label>
                {bulkFormData.contract_id !== null && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs text-gray-500" onClick={() => setBulkFormData(p => ({...p, contract_id: null}))}>
                    Annulla modifica
                  </Button>
                )}
              </div>
              {bulkFormData.contract_id === null ? (
                <Button variant="outline" size="sm" className="w-full text-gray-500" onClick={() => setBulkFormData(p => ({...p, contract_id: ''}))}>
                  Clicca per modificare Contratto
                </Button>
              ) : (
                <Combobox
                  options={contracts.map(c => ({ value: c.id, label: c.name }))}
                  value={bulkFormData.contract_id}
                  onChange={(v) => setBulkFormData(p => ({...p, contract_id: v}))}
                  placeholder="Seleziona contratto"
                  searchPlaceholder="Cerca contratto..."
                  emptyText="Nessun contratto trovato"
                />
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Prodotto</Label>
                {bulkFormData.product_id !== null && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs text-gray-500" onClick={() => setBulkFormData(p => ({...p, product_id: null}))}>
                    Annulla modifica
                  </Button>
                )}
              </div>
              {bulkFormData.product_id === null ? (
                <Button variant="outline" size="sm" className="w-full text-gray-500" onClick={() => setBulkFormData(p => ({...p, product_id: ''}))}>
                  Clicca per modificare Prodotto
                </Button>
              ) : (
                <Combobox
                  options={products.map(p => ({ value: p.id, label: p.name }))}
                  value={bulkFormData.product_id}
                  onChange={(v) => setBulkFormData(p => ({...p, product_id: v}))}
                  placeholder="Seleziona prodotto"
                  searchPlaceholder="Cerca prodotto..."
                  emptyText="Nessun prodotto trovato"
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>Annulla</Button>
            <Button
              onClick={handleBulkUpdate}
              disabled={bulkSaving || (bulkFormData.pm_ids === null && bulkFormData.supervisor_ids === null && bulkFormData.contract_id === null && bulkFormData.product_id === null)}
              data-testid="bulk-update-confirm"
            >
              {bulkSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Aggiorna {selectedIds.size} vendite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
